/**
 * src/pages/public/BookingPortal.jsx — the client-facing booking page.
 *
 * A linear three-step flow rather than one long form, because the middle step
 * depends on the first: slots cannot be shown until an artist and a date are
 * chosen. Steps are: pick an artist → pick a slot → enter details and pay.
 *
 * The page is branded with the studio's accent colour, since a client reaching
 * it from the studio's Instagram should not feel handed off to a third party.
 *
 * Nothing is booked here. Submitting creates a pending hold and redirects to
 * Stripe; the appointment is only confirmed when the deposit clears.
 *
 * REDESIGN.md Phase 4: restyled onto Stencil's tokens and components/ui/, the
 * registration-mark motif on whichever panel is the client's current task,
 * and a "Bookings by Tattoo Lab" footer credit — no change to what this page
 * fetches, sends, or when. Deposit terms are shown inline in the step-2 and
 * step-3 panels (never behind a click), which is all REDESIGN.md's "deposit
 * and cancellation terms visible before payment" can honestly mean here: the
 * product has no cancellation-policy field anywhere (checked — nothing in
 * `studios`, `settings`, or the API response), so no cancellation copy is
 * invented. Flagged in docs/redesign/progress.md, not silently papered over.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { useApi } from '../../lib/useApi.js';
import { formatMoney, formatTime } from '../../lib/format.js';
import { studioSlugFromHostname } from '../../lib/tenancy.js';
import { openNativePicker } from '../../lib/picker.js';
import { applyStudioAccent, clearStudioAccent } from '../../lib/studio-accent.js';
import { ErrorBanner, Empty, Loader } from '../../components/ui.jsx';
import { Button, Input, Textarea, PortalHeader } from '../../components/ui/index.js';
import PaymentForm from '../../components/PaymentForm.jsx';
import PortalFooter from './PortalFooter.jsx';
import './portal.css';

/**
 * Today as YYYY-MM-DD, in a given zone.
 *
 * `en-CA` conveniently formats as YYYY-MM-DD, so this needs no manual
 * assembly. Falls back to the browser's own zone when none is given, for the
 * brief window before the studio's timezone is known (see BookingPortal's
 * effect that corrects the default once it loads).
 *
 * @param {string} [timeZone]
 * @returns {string}
 */
function todayIso(timeZone) {
  return new Intl.DateTimeFormat('en-CA', timeZone ? { timeZone } : undefined).format(new Date());
}

export default function BookingPortal() {
  // The legacy `:slug`-in-path route param, or — once this studio has its
  // own subdomain (TS-28) — the slug implied by the page's own hostname.
  const { slug: pathSlug } = useParams();
  const slug = pathSlug ?? studioSlugFromHostname(window.location.hostname, import.meta.env.VITE_APP_BASE_DOMAIN);
  const [params] = useSearchParams();

  const studio = useApi(`/api/public/${slug}`);
  const artists = useApi(`/api/public/${slug}/artists`);

  const [artistId, setArtistId] = useState('');
  const [date, setDate] = useState(todayIso());
  const dateTouched = useRef(false);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', description: '' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // Set once the booking request comes back with a client secret, which means
  // the studio is on embedded payments. The redirect path never sets it.
  const [payment, setPayment] = useState(null);
  const [paid, setPaid] = useState(false);

  const availability = useApi(
    artistId ? `/api/public/${slug}/availability?artistId=${artistId}&date=${date}` : null,
    [artistId, date],
  );

  const branding = studio.data?.studio;

  useEffect(() => {
    applyStudioAccent(branding?.accentColor);
    return clearStudioAccent;
  }, [branding?.accentColor]);

  // The initial `date` default is computed before the studio's timezone is
  // known (see todayIso's fallback), which can be wrong near midnight in
  // either zone. Correct it once the real zone arrives — unless the visitor
  // has already picked a date themselves, which this must never overwrite.
  useEffect(() => {
    if (branding?.timezone && !dateTouched.current) {
      setDate(todayIso(branding.timezone));
    }
  }, [branding?.timezone]);

  // Changing artist or date invalidates the chosen slot.
  useEffect(() => setSlot(null), [artistId, date]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/api/public/${slug}/booking-request`, {
        clientName: form.name,
        clientEmail: form.email,
        clientPhone: form.phone || undefined,
        artistId,
        startsAt: slot.startsAt,
        description: form.description || undefined,
      });

      // Two shapes, depending on the studio's payment setting. A client secret
      // means the card is collected here; a checkout URL means Stripe's own
      // page. Either way the appointment is already held as 'pending' and is
      // only confirmed by the webhook.
      if (res.clientSecret) {
        setPayment({ clientSecret: res.clientSecret, amount: res.amount });
        setBusy(false);
        return;
      }
      window.location.assign(res.checkoutUrl);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  // Returning from a successful Checkout, or just paid inline.
  if (params.get('appointment') || paid) {
    return (
      <div className="portal-page">
        <main className="portal-main">
          <div className="portal-panel" style={{ textAlign: 'center' }}>
            <h2 className="portal-panel-title">You&rsquo;re booked in</h2>
            <p>
              Your deposit has gone through. Check your email for confirmation and your consent form —
              please sign it before you come in.
            </p>
          </div>
        </main>
        <PortalFooter />
      </div>
    );
  }

  const step = !artistId ? 1 : !slot ? 2 : 3;
  const stepLabel = step === 1 ? 'Choose an artist' : step === 2 ? 'Pick a time' : 'Your details';

  return (
    <div className="portal-page">
      <main className="portal-main">
        <Loader loading={studio.loading} error={studio.error}>
          {branding ? (
            <>
              <PortalHeader
                studioName={branding.name}
                step={{ current: step, total: 3, label: stepLabel }}
              />
              {branding.logoUrl ? (
                <div style={{ textAlign: 'center', marginTop: -16, marginBottom: 16 }}>
                  <img src={branding.logoUrl} alt="" style={{ maxHeight: 56 }} />
                </div>
              ) : null}
              <p className="portal-lead" style={{ textAlign: 'center', marginBottom: 20 }}>
                A {formatMoney(branding.defaultDepositAmount, branding.currency)} deposit secures your
                slot and comes off the price on the day.
              </p>

              {params.get('cancelled') ? (
                <div className="alert" style={{ marginBottom: 16 }}>
                  Payment was cancelled — nothing has been booked. Pick a slot to try again.
                </div>
              ) : null}
              {!branding.payoutsReady ? (
                <div className="alert" style={{ marginBottom: 16 }}>
                  This studio isn&rsquo;t accepting online deposits yet — check back soon.
                </div>
              ) : null}

              <section className={`portal-panel${step === 1 ? ' portal-panel--current' : ''}`}>
                {step === 1 ? (
                  <>
                    <span className="portal-reg-mark portal-reg-mark--tl" aria-hidden="true" />
                    <span className="portal-reg-mark portal-reg-mark--tr" aria-hidden="true" />
                  </>
                ) : null}
                <h2 className="portal-panel-title">Choose an artist</h2>
                <Loader loading={artists.loading} error={artists.error}>
                  {(artists.data?.artists ?? []).length === 0 ? (
                    <Empty>This studio has no artists taking bookings right now.</Empty>
                  ) : (
                    <div className="stack">
                      {artists.data.artists.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          className={`btn${artistId === a.id ? ' btn-primary' : ''}`}
                          style={{ textAlign: 'left', padding: 14, height: 'auto' }}
                          aria-pressed={artistId === a.id}
                          onClick={() => setArtistId(a.id)}
                        >
                          <span className="row">
                            {a.avatarUrl ? (
                              <img
                                src={a.avatarUrl}
                                alt=""
                                style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                              />
                            ) : null}
                            <span>
                              <strong>{a.name}</strong>
                              {a.bio ? (
                                <span style={{ display: 'block', fontWeight: 400, fontSize: 15 }}>{a.bio}</span>
                              ) : null}
                              {a.instagramHandle ? (
                                <span className="portal-lead" style={{ display: 'block', fontSize: 14 }}>
                                  {a.instagramHandle}
                                </span>
                              ) : null}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </Loader>
              </section>

              {artistId ? (
                <section className={`portal-panel${step === 2 ? ' portal-panel--current' : ''}`}>
                  {step === 2 ? (
                    <>
                      <span className="portal-reg-mark portal-reg-mark--tl" aria-hidden="true" />
                      <span className="portal-reg-mark portal-reg-mark--tr" aria-hidden="true" />
                    </>
                  ) : null}
                  <div className="row-between" style={{ marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
                    <h2 className="portal-panel-title" style={{ marginBottom: 0 }}>
                      Pick a time
                    </h2>
                    <label className="ui-visually-hidden" htmlFor="booking-date">
                      Date
                    </label>
                    <input
                      id="booking-date"
                      type="date"
                      value={date}
                      min={todayIso(branding.timezone)}
                      onClick={openNativePicker}
                      onChange={(e) => {
                        dateTouched.current = true;
                        setDate(e.target.value);
                      }}
                      style={{ width: 180 }}
                    />
                  </div>

                  <Loader loading={availability.loading} error={availability.error}>
                    {(availability.data?.slots ?? []).length === 0 ? (
                      <Empty>Nothing free that day. Try another date.</Empty>
                    ) : (
                      <div className="portal-slot-grid">
                        {availability.data.slots.map((s) => (
                          <button
                            key={s.startsAt}
                            type="button"
                            className={`btn slot${slot?.startsAt === s.startsAt ? ' selected' : ''}`}
                            aria-pressed={slot?.startsAt === s.startsAt}
                            onClick={() => setSlot(s)}
                          >
                            {formatTime(s.startsAt, branding.timezone)}
                          </button>
                        ))}
                      </div>
                    )}
                  </Loader>
                  {availability.data?.sessionMinutes ? (
                    <p className="portal-terms">
                      Sessions are {availability.data.sessionMinutes / 60} hours. If your piece needs
                      longer, book the first session and your artist will plan the rest with you.
                    </p>
                  ) : null}
                  <p className="portal-terms">
                    A {formatMoney(branding.defaultDepositAmount, branding.currency)} deposit is due on
                    the next step to hold this slot.
                  </p>
                </section>
              ) : null}

              {slot && payment ? (
                <section className="portal-panel portal-panel--current">
                  <span className="portal-reg-mark portal-reg-mark--tl" aria-hidden="true" />
                  <span className="portal-reg-mark portal-reg-mark--tr" aria-hidden="true" />
                  <h2 className="portal-panel-title">Payment</h2>
                  <p className="portal-terms" style={{ marginTop: 0, marginBottom: 14 }}>
                    Holding {formatTime(slot.startsAt, branding.timezone)} for you. Your slot is
                    confirmed once the deposit clears.
                  </p>
                  <PaymentForm
                    publishableKey={branding.stripePublishableKey}
                    clientSecret={payment.clientSecret}
                    amount={payment.amount}
                    currency={branding.currency}
                    accentColor={branding.accentColor}
                    onSuccess={() => setPaid(true)}
                  />
                </section>
              ) : slot ? (
                <section className="portal-panel portal-panel--current">
                  <span className="portal-reg-mark portal-reg-mark--tl" aria-hidden="true" />
                  <span className="portal-reg-mark portal-reg-mark--tr" aria-hidden="true" />
                  <h2 className="portal-panel-title">Your details</h2>
                  <form className="stack" onSubmit={submit}>
                    <ErrorBanner>{error}</ErrorBanner>

                    <Input
                      id="bname"
                      label="Name"
                      value={form.name}
                      onChange={set('name')}
                      required
                      autoComplete="name"
                    />
                    <div className="row wrap">
                      <div className="grow">
                        <Input
                          id="bemail"
                          label="Email"
                          type="email"
                          value={form.email}
                          onChange={set('email')}
                          required
                          autoComplete="email"
                        />
                      </div>
                      <div className="grow">
                        <Input
                          id="bphone"
                          label="Phone"
                          value={form.phone}
                          onChange={set('phone')}
                          autoComplete="tel"
                        />
                      </div>
                    </div>
                    <Textarea
                      id="bdesc"
                      label="What are you thinking of?"
                      value={form.description}
                      onChange={set('description')}
                      placeholder="Subject, size, placement, any references you have."
                    />

                    <p className="portal-terms" style={{ marginTop: 0 }}>
                      A {formatMoney(branding.defaultDepositAmount, branding.currency)} deposit is
                      taken now to secure this slot — the rest is due on the day.
                    </p>

                    <Button type="submit" variant="primary" disabled={busy || !branding.payoutsReady}>
                      {busy
                        ? 'Redirecting…'
                        : `Pay ${formatMoney(branding.defaultDepositAmount, branding.currency)} deposit`}
                    </Button>
                    <p className="portal-terms" style={{ marginBottom: 0 }}>
                      You&rsquo;ll be taken to Stripe to pay. Your slot is held while you check out and
                      confirmed once the deposit clears.
                    </p>
                  </form>
                </section>
              ) : null}
            </>
          ) : null}
        </Loader>
      </main>
      <PortalFooter />
    </div>
  );
}
